import React, { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { getOptions, getFirstOption } from "utils/fields";
import { getAllSubcomponents } from "utils/enclosure/components";
import { required, maxLength, getValidation } from "utils/fieldValidation";
import { windowCodeLogic } from "utils/codes";
import { getModelDataComponentPath } from "utils/enclosure/codeDrawerMeasurements";
import { getSelectedComponents } from "utils/parametric";
import windowCodeTemplate from "features/Model/Enclosure/Templates/windowCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import MultiSelect from "components/Input/MultiSelect";
import Checkbox from "components/Input/Checkbox";
import Field from "components/Field";
import Button from "components/Button";
import Tooltip from "components/Tooltip";
import PercentGradeSwitch from "components/EmbodiedCarbonGrade/PercentGradeSwitch";
import {
    EmbodiedCarbonCalculationsSummary,
    EmbodiedCarbonStandardWindowCodeGrade,
} from "components/EmbodiedCarbonGrade";

import globalStyles from "components/globalStyles.module.scss";
import CodeLibraryDestination from "components/CodeLibraryDestination";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode, getWindowRValue } = enclosureActions;
const { updateUpgradeCodes } = upgradeActions;

const charLim32 = maxLength(32);

const labelValidation = [required, charLim32];
const glazingValidation = getValidation("csWindowGlazingType");
const coatingTintValidation = getValidation("csWindowCoatingTint");
const fillValidation = getValidation("csWindowFillType");
const spacerValidation = getValidation("csWindowSpacerType");
const typeValidation = getValidation("csWindowType");
const frameMatValidation = getValidation("csWindowFrameMaterial");

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

/** Bordered stack for glazing + frame / coatings / fill / spacer / type (matches other enclosure framing-group UIs). */
const WINDOW_CODE_BORDER = "#e1eaf0";
const windowCodeFramingRowStyle = {
    top: {
        gridGap: "0",
        border: `1px solid ${WINDOW_CODE_BORDER}`,
        borderBottom: "none",
        borderRadius: "4px 4px 0px 0px",
    },
    middle: {
        gridGap: "0",
        borderLeft: `1px solid ${WINDOW_CODE_BORDER}`,
        borderRight: `1px solid ${WINDOW_CODE_BORDER}`,
        borderTop: "none",
        borderBottom: "none",
    },
    bottom: {
        gridGap: "0",
        borderBottom: `1px solid ${WINDOW_CODE_BORDER}`,
        borderLeft: `1px solid ${WINDOW_CODE_BORDER}`,
        borderRight: `1px solid ${WINDOW_CODE_BORDER}`,
        borderTop: "none",
        borderRadius: "0px 0px 4px 4px",
    },
};

const StandardWindowCode = React.memo(
    ({
        toggleOpen,
        componentId,
        fieldAccessor,
        windowAccessor = fieldAccessor,
        isCodeLibrary = false,
        change,
        upgradeWhereToAddCode = {},
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        removeFormErrors,
        CodeTypeSelect,
    }) => {
        const [barMetric, setBarMetric] = useState("percentage");

        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};
        const selectedComponentsForUpgrade = useStateSelector(`newForm.upgradeDrawer.selectedComponents`) || [];

        const uid = useStateSelector(`user.uid`);
        const { windowType = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { modelId, modelData: { codes: modelCodes = {}, components = {} } = {} } =
            useStateSelector(`model`) || {};
        const { components: componentsFromParametric = {} } =
            useStateSelector(`parametricAnalysis.parametricAnalysis.model.modelData`) || {};

        const windowCode = useStateSelector(`${drawerAccessor}.windowCode`) || {};
        const { id: windowGlazingTypeId, value: windowGlazingTypeValue } =
            useStateSelector(`${drawerAccessor}.windowCode.layers.glazingType`) || {};
        const { value: windowCoatingsTintsValue } =
            useStateSelector(`${drawerAccessor}.windowCode.layers.coatingsTints`) || {};
        const { value: windowFillTypeValue } = useStateSelector(`${drawerAccessor}.windowCode.layers.fillType`) || {};
        const { value: windowSpacerTypeValue } =
            useStateSelector(`${drawerAccessor}.windowCode.layers.spacerType`) || {};
        const { id: windowTypeId, value: windowTypeValue } =
            useStateSelector(`${drawerAccessor}.windowCode.layers.windowType`) || {};
        const { id: windowFrameMatId, value: windowFrameMatValue } =
            useStateSelector(`${drawerAccessor}.windowCode.layers.frameMaterial`) || {};
        const windowLayers = useStateSelector(`${drawerAccessor}.windowCode.layers`) || {};
        const windowLabel = useStateSelector(`${drawerAccessor}.windowCode.label`) || "";

        const layersFieldPrefix = `${drawerAccessor}.windowCode.layers`;

        const windowValue = `${windowGlazingTypeValue}${windowCoatingsTintsValue}${windowFillTypeValue}${windowSpacerTypeValue}${windowTypeValue}${windowFrameMatValue}`;

        const isSingleGlazing = parseFloat(windowGlazingTypeId) === 0;

        const makeSpacerFillDefault = windowGlazingTypeValue === "1";

        const { codeRef: editingCodeRef = null, isLibCode = false } = windowType;
        const isEditing = !!editingCodeRef;
        const isUpgradeComponent = windowAccessor.includes("fields");

        const componentPath = isUpgradeComponent ? "" : getModelDataComponentPath("Window", windowAccessor);
        const selectedComponents = isUpgradeComponent ? selectedComponentsForUpgrade : [componentPath];

        const { upgradeSubType, upgradeType } = upgradeWhereToAddCode?.ecmInputMapUpgrade || {};

        const embcModelComponents = modelId ? components : componentsFromParametric;
        const embcSelectedComponents = isEqual(selectedComponents, ["*"])
            ? getSelectedComponents(upgradeSubType, upgradeType, embcModelComponents)
            : selectedComponents;

        const valueRef = useRef(null);

        const handleGlazingChange = useCallback(
            ({ id, value }) => {
                change(
                    `${drawerAccessor}.windowCode.layers.coatingsTints`,
                    getFirstOption({ indKey: id, depKey: "csWindowCoatingTint" })
                );

                //Reset Fill type and spacer type whenever the glazing type is changed. Matching the values is currently
                change(
                    `${drawerAccessor}.windowCode.layers.fillType`,
                    getFirstOption({ indKey: id, depKey: "csWindowFillType" })
                );
                change(
                    `${drawerAccessor}.windowCode.layers.spacerType`,
                    getFirstOption({ indKey: id, depKey: "csWindowSpacerType" })
                );
            },
            [windowGlazingTypeValue, change]
        );

        useEffect(() => {
            const initialValues = {
                windowCode: isEmpty(windowType) ? windowCodeTemplate : windowType,
                addToLibrary: isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        useEffect(() => {
            valueRef.current = windowValue;
        }, [windowValue, windowLabel]);

        //Window Type Options -> Dependent on Spacer Type
        let windowTypeOpts = getOptions({ fieldKey: "csWindowType" });
        if (windowSpacerTypeValue !== "0") {
            windowTypeOpts = windowTypeOpts.filter((opt) => opt.value.value !== "3");
        }

        //Frame Material Options -> Dependent on Window Type
        const frameOpts = useMemo(() => {
            let opts = getOptions({ fieldKey: "csWindowFrameMaterial", indKey: windowGlazingTypeId });
            if (windowTypeValue === "3") {
                opts = opts.filter((opt) => ["0", "1", "2", "3"].includes(opt.value.value));
            } else if (windowTypeValue === "4") {
                if (["3", "4", "6", "7", "8"].includes(windowGlazingTypeValue)) {
                    //Can't have frame 1
                    opts = opts.filter((opt) => opt.value.value !== "1");
                }
                opts = opts.filter((opt) => opt.value.value !== "4");
            } else if (windowTypeValue === "5") {
                opts = opts.filter((opt) => opt.value.value !== "6");
            }
            return opts;
        }, [windowGlazingTypeId, windowGlazingTypeValue, windowTypeValue]);

        const frameSelectionInvalid = !frameOpts.map(({ value }) => value.id).includes(windowFrameMatId);
        const windowTypeSelectionInvalid = !windowTypeOpts.map(({ value }) => value.id).includes(windowTypeId);

        useEffect(() => {
            // Reset frame material if option is not in the frame options list.
            const frameMaterial = { id: windowFrameMatId, value: windowFrameMatValue };
            const optionMatches = frameOpts.filter(
                (opt) => opt.value.value === frameMaterial.value && opt.value.id === frameMaterial.id
            );

            if (isEmpty(optionMatches)) {
                change(
                    `${drawerAccessor}.windowCode.layers.frameMaterial`,
                    getFirstOption({ indKey: windowGlazingTypeId, depKey: "csWindowFrameMaterial" })
                );
            }
        }, [windowFrameMatId, windowFrameMatValue, windowGlazingTypeId, frameOpts, change]);

        let availableComponents = getAllSubcomponents("window", components, windowAccessor).sort(codeSort);

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        return (
            <div className={globalStyles.standardCodeWrapper}>
                <h3>Standard Code</h3>
                <InputRow
                    gridTemplate={CodeTypeSelect || (!isCodeLibrary && !isUpgradeComponent) ? "1fr 1fr 1fr" : "1fr 1fr"}
                    style={{ alignItems: "flex-end" }}
                >
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.windowCode.label`}
                        label="Code Label"
                        placeholder="Code Label"
                        validate={labelValidation}
                        setValue={windowLabel.toString() === valueRef.current ? windowValue : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.windowCode.value`}
                        label="Internal Code"
                        placeholder="###########"
                        disabled={true}
                        setValue={windowValue}
                    />
                    {!isCodeLibrary && !isUpgradeComponent && (
                        <Field
                            component={MultiSelect}
                            name={`${drawerAccessor}.selectedComponents`}
                            label={`Apply to multiple components`}
                            options={availableComponents.map(({ disabled = false, label = "", ...rest }) => ({
                                ...rest,
                                disabled,
                                label: disabled ? `${label} (current component)` : label,
                            }))}
                            hasSelectAll
                        />
                    )}
                    {!isCodeLibrary && (
                        <>
                            <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                                <CodeLibraryDestination
                                    drawerAccessor={drawerAccessor}
                                    editingCodeRef={editingCodeRef}
                                    isLibCode={isLibCode}
                                />
                            </div>
                            <Field
                                component={Checkbox}
                                name={`${drawerAccessor}.setAsModelDefault`}
                                label={"Set as model default"}
                                large
                                type="checkbox"
                                style={{ whiteSpace: "nowrap" }}
                            />
                            <Tooltip id="codeLibTip" hide={!isLibCode} />
                        </>
                    )}
                </InputRow>

                <div className={globalStyles.assemblyLayersHeading}>
                    <h4>Assembly Layers</h4>
                    <PercentGradeSwitch activeTab={barMetric} setActiveTab={setBarMetric} />
                </div>

                <div className={globalStyles.framingGroup}>
                    <InputRow style={windowCodeFramingRowStyle.top} lastRow={true}>
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.windowCode.layers.glazingType`}
                            label="Glazing Type"
                            placeholder="Choose Glazing Type"
                            options={getOptions({ fieldKey: "csWindowGlazingType" })}
                            validate={glazingValidation}
                            onChange={handleGlazingChange}
                            isFlatDesign={true}
                            borderBottom={true}
                            customBorderRadius={{ borderRadius: "4px 4px 0px 0px" }}
                        />
                    </InputRow>
                    <InputRow style={windowCodeFramingRowStyle.middle} lastRow={true}>
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.windowCode.layers.coatingsTints`}
                            label="Coatings / Tints"
                            placeholder="Choose Coatings / Tints"
                            options={getOptions({ fieldKey: "csWindowCoatingTint", indKey: windowGlazingTypeId })}
                            validate={coatingTintValidation}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <InputRow style={windowCodeFramingRowStyle.middle} lastRow={true}>
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.windowCode.layers.fillType`}
                            label="Fill Type"
                            placeholder="Choose Fill Type"
                            options={getOptions({ fieldKey: "csWindowFillType", indKey: windowGlazingTypeId })}
                            validate={fillValidation}
                            disabled={isSingleGlazing || makeSpacerFillDefault}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <InputRow style={windowCodeFramingRowStyle.middle} lastRow={true}>
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.windowCode.layers.spacerType`}
                            label="Spacer Type"
                            placeholder="Choose Spacer Type"
                            options={getOptions({ fieldKey: "csWindowSpacerType", indKey: windowGlazingTypeId })}
                            validate={spacerValidation}
                            disabled={isSingleGlazing || makeSpacerFillDefault}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <InputRow style={windowCodeFramingRowStyle.bottom} lastRow={true}>
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.windowCode.layers.windowType`}
                            label="Window Type"
                            placeholder="Choose Window Type"
                            options={windowTypeOpts}
                            validate={typeValidation}
                            warning={windowTypeSelectionInvalid && "invalidWindowType"}
                            isFlatDesign={true}
                            customBorderRadius={{ borderRadius: "0px 0px 4px 4px" }}
                        />
                    </InputRow>
                    <InputRow style={windowCodeFramingRowStyle.bottom} lastRow={true}>
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.windowCode.layers.frameMaterial`}
                            label="Frame Material"
                            placeholder="Choose Frame Material"
                            options={frameOpts}
                            validate={frameMatValidation}
                            warning={frameSelectionInvalid && "invalidWindowFrame"}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <EmbodiedCarbonStandardWindowCodeGrade
                        layers={windowLayers}
                        change={change}
                        layersFieldPrefix={layersFieldPrefix}
                        barMetric={barMetric}
                    />
                </div>

                <EmbodiedCarbonCalculationsSummary
                    componentId={componentId}
                    code={windowCode}
                    componentType={"window"}
                    modelDataComponents={embcModelComponents}
                    selectedComponents={embcSelectedComponents}
                />

                <div
                    style={{
                        borderBottom: `1px solid ${WINDOW_CODE_BORDER}`,
                        marginTop: "1.5rem",
                        marginBottom: "0.5rem",
                    }}
                />

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            toggleOpen(false);
                            removeFormErrors(`${drawerAccessor}.windowCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                toggleOpen,
                                uid,
                                windowAccessor,
                                editingCodeRef,
                                isCodeLibrary,
                                componentId,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear wall code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "windowType")),
    handleSubmit: ({
        change,
        codeFields,
        toggleOpen,
        uid,
        windowAccessor,
        editingCodeRef,
        isCodeLibrary,
        componentId,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const {
            windowCode = {},
            addToLibrary = false,
            orgLibraryId = "",
            setAsModelDefault = false,
            selectedComponents = [],
        } = codeFields;
        const { codeRef, value, nominalRValue = null, label, isLibCode, ...rest } = windowCode;

        toggleOpen(false);

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        windowCodeLogic({
            change,
            componentCodeType: "Window",
            componentId,
            code: windowCode,
            codeType: "S",
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            getWindowRValue,
            fieldAccessor: windowAccessor,
            components,
            modelCodes,
            addToLibrary,
            orgLibraryId,
            addToCodeLib,
            uid,
            deleteCode,
            updateUpgradeCodes,
            setAsModelDefault,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardWindowCode);
